import  {
  type PlatformInsightsOnboardingDistrictDto,
  type PlatformInsightsOnboardingResponseDto,
} from "@crowdcoursing/api/openapi";

export type DistrictOnboardingStatus = "Launched" | "Not started" | "Onboarding" | "Starting";

/**
 * District-level onboarding status for Platform Insights.
 *
 * - Not started: zero people with roles in the district bubble tree (school count is ignored for this bucket).
 * - Onboarding: at least one school, average people per school is strictly below 15.
 * - Launched: at least one school, average people per school is strictly above 15.
 * - Starting: all other cases (e.g. people with roles but no schools in this dataset, or average exactly 15).
 */
export function deriveDistrictOnboardingStatus(input: {
  peopleCount: number;
  schoolCount: number;
}): DistrictOnboardingStatus {
  const { peopleCount, schoolCount } = input;
  if (peopleCount === 0) {
    return "Not started";
  }
  if (schoolCount > 0) {
    const averagePeoplePerSchool = peopleCount / schoolCount;
    if (averagePeoplePerSchool < 15) {
      return "Onboarding";
    }
    if (averagePeoplePerSchool > 15) {
      return "Launched";
    }
  }
  return "Starting";
}

export interface DistrictOnboardingGridRow {
  bubbleId: string;
  districtName: string;
  peopleOnboardedCount: number;
  rowId: string;
  schoolCount: number;
  schoolsOnboardedCount: number;
  status: DistrictOnboardingStatus;
  /** District payload from the onboarding API (schools, people) for drill-down UI. */
  source: PlatformInsightsOnboardingDistrictDto;
}

/**
 * Builds onboarding grid rows from the site-admin Platform Insights district-onboarding API payload.
 *
 * `peopleOnboardedCount` is the number of distinct users with at least one role on the district bubble or any
 * descendant bubble in that district tree (same scope as the API’s `districtTreePeople`).
 *
 * A school counts as onboarded when it has at least one user with a role in that school’s bubble subtree
 * (`people` on each school in the response).
 */
export function buildDistrictOnboardingGridRows(
  response: PlatformInsightsOnboardingResponseDto | null | undefined,
): DistrictOnboardingGridRow[] {
  const districts = response?.districts;
  if (!districts?.length) {
    return [];
  }

  return districts
    .filter((d): d is PlatformInsightsOnboardingDistrictDto & { schoolDistrictId: string; bubbleId: string } =>
      Boolean(d.schoolDistrictId && d.bubbleId),
    )
    .map((d) => {
      const schools = d.schools ?? [];
      const districtTreePeople = d.districtTreePeople ?? [];
      const schoolCount = schools.length;
      const peopleOnboardedCount = districtTreePeople.length;
      const schoolsOnboardedCount = schools.filter((s) => (s.people?.length ?? 0) > 0).length;
      const status = deriveDistrictOnboardingStatus({ peopleCount: peopleOnboardedCount, schoolCount });

      return {
        bubbleId: d.bubbleId,
        districtName: d.name ?? "",
        peopleOnboardedCount,
        rowId: d.schoolDistrictId,
        schoolCount,
        schoolsOnboardedCount,
        source: d,
        status,
      };
    })
    .sort((a, b) => a.districtName.localeCompare(b.districtName, undefined, { sensitivity: "base" }));
}
